import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/Header';
import BulkUploadQueue from '../components/BulkUploadQueue';
import { convertToMp3, needsConversion, convertVideoToAudio } from '../utils/audioConverter';
import './BulkUpload.css';

const BulkUpload = () => {
  const navigate = useNavigate();
  const { isAuthenticated, authLoading } = useAuth();
  const [uploadQueue, setUploadQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [globalProgress, setGlobalProgress] = useState({ completed: 0, total: 0, failed: 0 });
  const fileInputRef = useRef(null);

  // Redirect to auth if not authenticated
  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileSelection(files);
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    handleFileSelection(files);
    // Reset input
    e.target.value = '';
  }, []);

  const handleFileSelection = useCallback((files) => {
    const audioVideoFiles = files.filter(file => {
      const isAudio = file.type.startsWith('audio/');
      const isVideo = file.type.startsWith('video/');
      const hasValidExtension = /\.(mp3|wav|ogg|m4a|aac|flac|wma|opus|mp4|mov|avi|mkv|webm|flv|wmv|m4v|3gp|ogv|mpg|mpeg|ts|vob)$/i.test(file.name);
      return isAudio || isVideo || hasValidExtension;
    });

    if (audioVideoFiles.length === 0) {
      alert('Please select valid audio or video files');
      return;
    }

    // Add all files to queue
    const newItems = audioVideoFiles.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      status: 'pending',
      progress: 0,
      error: null,
      startedAt: null,
      completedAt: null
    }));

    setUploadQueue(prev => [...prev, ...newItems]);
    setGlobalProgress(prev => ({ ...prev, total: prev.total + newItems.length }));

    // Start processing immediately
    newItems.forEach(item => processFile(item));
  }, []);

  const processFile = async (queueItem) => {
    try {
      // Update status to processing
      setUploadQueue(prev =>
        prev.map(item =>
          item.id === queueItem.id
            ? { ...item, status: 'processing', startedAt: Date.now(), progress: 10 }
            : item
        )
      );

      let processedFile = queueItem.file;
      let fileName = queueItem.file.name;

      // Check if conversion is needed
      if (needsConversion(queueItem.file)) {
        setUploadQueue(prev =>
          prev.map(item =>
            item.id === queueItem.id
              ? { ...item, progress: 20 }
              : item
          )
        );

        // Convert video to audio or audio format
        if (queueItem.file.type.startsWith('video/')) {
          processedFile = await convertVideoToAudio(queueItem.file);
        } else {
          processedFile = await convertToMp3(queueItem.file);
        }
        fileName = processedFile.name;

        setUploadQueue(prev =>
          prev.map(item =>
            item.id === queueItem.id
              ? { ...item, progress: 50 }
              : item
          )
        );
      }

      // Upload and transcribe
      setUploadQueue(prev =>
        prev.map(item =>
          item.id === queueItem.id
            ? { ...item, progress: 70 }
            : item
        )
      );

      const formData = new FormData();
      formData.append('audio', processedFile);
      formData.append('name', queueItem.name);

      // Add diarization preference if enabled
      const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
      if (preferences.enableDiarization === true) {
        formData.append('enableDiarization', 'true');
      }

      const response = await fetch('/api/transcribe', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Update to completed
      setUploadQueue(prev =>
        prev.map(item =>
          item.id === queueItem.id
            ? {
                ...item,
                status: 'completed',
                progress: 100,
                completedAt: Date.now(),
                transcriptId: data.transcriptId
              }
            : item
        )
      );

      setGlobalProgress(prev => ({ ...prev, completed: prev.completed + 1 }));

    } catch (error) {
      console.error('Upload error:', error);
      setUploadQueue(prev =>
        prev.map(item =>
          item.id === queueItem.id
            ? {
                ...item,
                status: 'failed',
                error: error.message,
                completedAt: Date.now()
              }
            : item
        )
      );

      setGlobalProgress(prev => ({ ...prev, failed: prev.failed + 1 }));
    }
  };

  const handleQueueRemove = useCallback((id) => {
    setUploadQueue(prev => {
      const item = prev.find(i => i.id === id);
      const newQueue = prev.filter(i => i.id !== id);

      // Update global progress
      setGlobalProgress(g => {
        const newProgress = { ...g, total: g.total - 1 };
        if (item?.status === 'completed') newProgress.completed = g.completed - 1;
        if (item?.status === 'failed') newProgress.failed = g.failed - 1;
        return newProgress;
      });

      return newQueue;
    });
  }, []);

  const handleQueueClear = useCallback(() => {
    // Only clear completed and failed
    setUploadQueue(prev => {
      const remaining = prev.filter(item =>
        item.status === 'pending' || item.status === 'processing'
      );
      const removed = prev.length - remaining.length;

      setGlobalProgress(g => ({
        total: g.total - removed,
        completed: 0,
        failed: 0
      }));

      return remaining;
    });
  }, []);

  const pendingCount = uploadQueue.filter(i => i.status === 'pending').length;
  const processingCount = uploadQueue.filter(i => i.status === 'processing').length;

  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="bulk-upload-page">
      <Header />

      <main className="bulk-upload-main">
        <div className="bulk-upload-container">
          <div className="bulk-upload-header">
            <h1>Bulk Upload</h1>
            <p>Upload multiple audio or video files for transcription</p>
          </div>

          {/* Global Progress */}
          {globalProgress.total > 0 && (
            <div className="global-progress">
              <div className="progress-stats">
                <span className="stat">
                  <strong>{globalProgress.completed}</strong> completed
                </span>
                <span className="stat">
                  <strong>{processingCount}</strong> processing
                </span>
                <span className="stat">
                  <strong>{pendingCount}</strong> pending
                </span>
                {globalProgress.failed > 0 && (
                  <span className="stat failed">
                    <strong>{globalProgress.failed}</strong> failed
                  </span>
                )}
                <span className="stat total">
                  <strong>{globalProgress.total}</strong> total
                </span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${(globalProgress.completed / globalProgress.total) * 100}%`
                  }}
                />
              </div>
            </div>
          )}

          {/* Drop Zone */}
          <div
            className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.ogg,.m4a,.aac,.flac,.wma,.opus,.mp4,.mov,.avi,.mkv,.webm,.flv,.wmv,.m4v,.3gp,.ogv,.mpg,.mpeg,.ts,.vob"
              onChange={handleFileInputChange}
              multiple
              style={{ display: 'none' }}
            />

            <div className="drop-zone-content">
              <div className="upload-icon">📁</div>
              <h2>Drop files here or click to browse</h2>
              <p>Supports audio and video files • No file limit</p>
              <p className="formats">
                MP3, WAV, M4A, MP4, MOV, AVI, and more
              </p>
            </div>
          </div>

          {/* Queue */}
          {uploadQueue.length > 0 && (
            <BulkUploadQueue
              files={uploadQueue}
              onRemove={handleQueueRemove}
              onClear={handleQueueClear}
            />
          )}

          {/* Help Text */}
          {uploadQueue.length === 0 && (
            <div className="help-text">
              <h3>How it works:</h3>
              <ol>
                <li>Select or drag & drop multiple files</li>
                <li>All files will process simultaneously</li>
                <li>Track progress for each file in real-time</li>
                <li>Find completed transcripts in your Dashboard</li>
              </ol>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BulkUpload;
